"use client";

import { c as _c } from "react/compiler-runtime";
import { classNames } from "@/lib/utils";
import { useRef, useCallback } from "react";
import { T } from "gt-next";
import MorphingLogo from "./MorphingLogo";

// Module-level definitions for stable references (prevents re-renders)
const LOGOS = [{
  src: "/img/clients/peloton.svg",
  alt: "Peloton - Leading interactive fitness platform"
}, {
  src: "/img/clients/prime.png",
  alt: "Prime - Leading hydration and energy drink brand"
}, {
  src: "/img/clients/erewhon.svg",
  alt: "Erewhon - Luxury organic market and cafe"
}, {
  src: "/img/clients/kylie-cosmetics.svg",
  alt: "Kylie Cosmetics - Premium beauty brand"
}, {
  src: "/img/clients/buzzfeed.svg",
  alt: "Buzzfeed - Digital media and news company"
}, {
  src: "/img/clients/gymshark.svg",
  alt: "Gymshark - Fitness apparel and accessories brand"
}, {
  src: "/img/clients/dyson.svg",
  alt: "Dyson - Innovation-led technology company"
}, {
  src: "/img/clients/hoka.png",
  alt: "Hoka - Premium performance footwear brand"
}] as const;

// Pre-computed logo groups for morph effect
const LOGO_GROUPS = LOGOS.map((logo, index) => [logo, LOGOS[(index + 4) % LOGOS.length]]);

// Duplicated for seamless infinite scroll
const ALL_LOGO_GROUPS = [...LOGO_GROUPS, ...LOGO_GROUPS];

// Get current translateX from computed transform matrix
function getTranslateX(el: HTMLElement): number {
  const style = window.getComputedStyle(el);
  const matrix = new DOMMatrixReadOnly(style.transform);
  return matrix.m41;
}
export default function LandingLogosCarousel(t0) {
  const $ = _c(24);
  const {
    isAuthPage
  } = t0;
  const trackRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      isDragging: false,
      startX: 0,
      startTranslateX: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dragState = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => {
      const track = trackRef.current;
      if (!track) {
        return;
      }
      const currentX = getTranslateX(track);
      dragState.current = {
        isDragging: true,
        startX: e.touches[0].clientX,
        startTranslateX: currentX
      };
      track.style.animationPlayState = "paused";
      track.style.transform = `translateX(${currentX}px)`;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleTouchStart = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => {
      if (!dragState.current.isDragging || !trackRef.current) {
        return;
      }
      const deltaX = e_0.touches[0].clientX - dragState.current.startX;
      const newX = dragState.current.startTranslateX + deltaX;
      trackRef.current.style.transform = `translateX(${newX}px)`;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleTouchMove = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const track_0 = trackRef.current;
      if (!track_0 || !dragState.current.isDragging) {
        return;
      }
      dragState.current.isDragging = false;
      const currentX_0 = getTranslateX(track_0);
      const halfWidth = track_0.scrollWidth / 2;
      let normalizedX = currentX_0 % halfWidth;
      if (normalizedX > 0) {
        normalizedX = normalizedX - halfWidth;
      }
      const progress = Math.abs(normalizedX) / halfWidth;
      const duration = window.innerWidth >= 1024 ? 50 : 30;
      const delay = -(progress * duration);
      track_0.style.transform = "";
      track_0.style.animationDelay = `${delay}s`;
      track_0.style.animationPlayState = "running";
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleTouchEnd = t4;
  const t5 = isAuthPage ? "absolute bottom-[10%] hidden animate-[fade-in_0.8s_linear_0.5s_forwards] overflow-hidden p-0 opacity-0 sm:block" : "p-0 pb-0 pt-12 sm:px-5 md:px-6 lg:px-8 lg:pb-[25px] lg:pt-16";
  let t6;
  if ($[4] !== t5) {
    t6 = classNames("z-20 mx-auto w-full max-w-7xl overflow-hidden", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== isAuthPage) {
    t7 = isAuthPage ? <p className="mb-4 text-center text-[12px] font-semibold uppercase tracking-wider text-ink/60"><T>Trusted by the world's best</T></p> : <h2 className="font-system pointer-events-none mx-5 max-w-lg text-center font-semibold uppercase hero-text-gradient bg-size-[170%] animate-gradient bg-clip-text text-transparent saturate-[1.2] sm:mx-auto sm:max-w-xl sm:text-lg">{"Trusted by 65,000+ Creators & Brands"}</h2>;
    $[6] = isAuthPage;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = isAuthPage ? "mt-6 bg-transparent" : "before:z-2 after:z-2 mt-8 bg-white before:absolute before:left-0 before:top-0 before:h-full before:w-[100px] before:bg-[linear-gradient(to_right,white_0%,rgba(255,255,255,0)_100%)] before:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[100px] after:-scale-x-100 after:bg-[linear-gradient(to_right,white_0%,rgba(255,255,255,0)_100%)] after:content-['']";
  let t9;
  if ($[8] !== t8) {
    t9 = classNames("group relative mx-auto w-full overflow-hidden overflow-y-visible sm:w-[1220px]", t8);
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== isAuthPage) {
    t10 = !isAuthPage && <><div style={{
        flex: "none",
        zIndex: 1
      }} className="absolute bottom-0 left-0 top-0 h-full w-[100px] sm:w-[270px]"><div style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 0
        }}><div className="logos-blur-layer" style={{
            zIndex: 1,
            backdropFilter: "blur(0.5px)",
            WebkitBackdropFilter: "blur(0.5px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 37.5%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 37.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 2,
            backdropFilter: "blur(0.1875px)",
            WebkitBackdropFilter: "blur(0.1875px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 0) 50%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 0) 50%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 3,
            backdropFilter: "blur(0.375px)",
            WebkitBackdropFilter: "blur(0.375px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 62.5%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 62.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 4,
            backdropFilter: "blur(0.75px)",
            WebkitBackdropFilter: "blur(0.75px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 0) 75%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 0) 75%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 5,
            backdropFilter: "blur(1.5px)",
            WebkitBackdropFilter: "blur(1.5px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 0) 87.5%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 0) 87.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 6,
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 0) 100%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 0) 100%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 7,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 1) 100%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 1) 100%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 8,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            maskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 87.5%, rgba(0, 0, 0, 1) 100%)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0, 0, 0, 0) 87.5%, rgba(0, 0, 0, 1) 100%)"
          }} /></div></div><div style={{
        flex: "none",
        position: "absolute",
        zIndex: 1
      }} className="absolute bottom-0 right-0 top-0 h-full w-[100px] sm:w-[270px]"><div style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 0
        }}><div className="logos-blur-layer" style={{
            zIndex: 1,
            backdropFilter: "blur(0.5px)",
            WebkitBackdropFilter: "blur(0.5px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 37.5%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 37.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 2,
            backdropFilter: "blur(0.1875px)",
            WebkitBackdropFilter: "blur(0.1875px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 0) 50%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 12.5%, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 0) 50%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 3,
            backdropFilter: "blur(0.375px)",
            WebkitBackdropFilter: "blur(0.375px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 62.5%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 1) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 62.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 4,
            backdropFilter: "blur(0.75px)",
            WebkitBackdropFilter: "blur(0.75px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 0) 75%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 37.5%, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 0) 75%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 5,
            backdropFilter: "blur(1.5px)",
            WebkitBackdropFilter: "blur(1.5px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 0) 87.5%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 0) 87.5%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 6,
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 0) 100%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 62.5%, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 0) 100%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 7,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 1) 100%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 1) 87.5%, rgba(0, 0, 0, 1) 100%)"
          }} /><div className="logos-blur-layer" style={{
            zIndex: 8,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            maskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 87.5%, rgba(0, 0, 0, 1) 100%)",
            WebkitMaskImage: "linear-gradient(to right, rgba(0, 0, 0, 0) 87.5%, rgba(0, 0, 0, 1) 100%)"
          }} /></div></div></>;
    $[10] = isAuthPage;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== isAuthPage) {
    t11 = ALL_LOGO_GROUPS.map((logoGroup, index) => <div key={index} className={classNames("slide flex h-[48px] w-auto max-w-[160px] items-center justify-center px-3 sm:h-[44px] sm:max-w-[140px] sm:px-5 lg:h-auto lg:max-w-full", isAuthPage ? "lg:w-[150px]" : "lg:w-[180px]")}><MorphingLogo logos={logoGroup} initialDelay={index % LOGO_GROUPS.length * 500} morphInterval={4000} width={160} height={48} imageClassName={isAuthPage ? "pointer-events-none opacity-60" : "pointer-events-none opacity-75"} /></div>);
    $[12] = isAuthPage;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = <div ref={trackRef} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchEnd} className="group-hover:animate-pause flex w-[calc(360px*10)] animate-infinite-scroll gap-0 duration-30000 touch-pan-y sm:gap-6 lg:duration-50000">{t11}</div>;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t10 || $[17] !== t12 || $[18] !== t9) {
    t13 = <div className={t9}>{t10}{t12}</div>;
    $[16] = t10;
    $[17] = t12;
    $[18] = t9;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t13 || $[21] !== t6 || $[22] !== t7) {
    t14 = <section className={t6}>{t7}{t13}</section>;
    $[20] = t13;
    $[21] = t6;
    $[22] = t7;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
}